import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * LanguageDropdown
 * - langs: [{ code, label, short, flag }]
 * - size: "sm" | "md"
 * - showLabel: boolean
 * - align: "left" | "right"
 */
export default function LanguageDropdown({
  langs,
  size = "sm",
  showLabel = false,
  align = "right",
  className = ""
}) {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const options = useMemo(() => {
    return (
      langs || [
        { code: "es", label: "Español", short: "ES", flag: "🇲🇽" },
        { code: "en", label: "English", short: "EN", flag: "🇺🇸" },
        { code: "de", label: "Deutsch",  short: "DE", flag: "🇩🇪" },
        { code: "fr", label: "Français", short: "FR", flag: "🇫🇷" },
        { code: "pt", label: "Português", short: "PT", flag: "🇧🇷" }
      ]
    );
  }, [langs]);

  const current = useMemo(() => {
    return options.find((x) => x.code === lang) || options[0];
  }, [options, lang]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (code) => {
    setLang(code);
    setOpen(false);
  };

  return (
     <>
    <div ref={wrapRef} className={`langDd ${size} ${className}`}>
      {showLabel ?<div className="langDd-label">Language</div> : null}    


      <button
        type="button"
        className="langDd-btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="langDd-flag">{current?.flag || "🌐"}</span>
        <span className="langDd-text">{current?.short || current?.code?.toUpperCase()}</span>
        <span className="langDd-caret">▾</span>
      </button>

      {open ? (
        <div className={`langDd-menu ${align === "left" ? "left" : "right"}`} role="menu">
          {options.map((o) => (
            <button
              key={o.code}
              type="button"
              className={`langDd-item ${o.code === lang ? "active" : ""}`}
              onClick={() => pick(o.code)}
              role="menuitem"
            >
              <span className="langDd-flag">{o.flag || "🌐"}</span>
              <span className="langDd-itemLabel">{o.label}</span>
              <span className="langDd-itemCode">{o.short || o.code.toUpperCase()}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>

    
      <style>{`
            .langSw{
            display: inline-flex;
            align-items: center;
            gap: 8px;
            }

            .langSw-label{
            font-size: .85rem;
            color: #fff; /* 👈 blanco */
            font-weight: 800;
            }

            .langSw-select{
            border: 1px solid rgba(2,6,23,.14);
            background: #fff;
            border-radius: 12px;
            padding: 5px 8px;   /* 👈 más pequeño */
            font-weight: 850;
            color: rgba(2,6,23,.92);
            outline: none;
            }

            .langSw.sm .langSw-select{ font-size: .85rem; }
            .langSw.md .langSw-select{ font-size: .95rem; padding: 7px 10px; }

            .langSw-chips{
            display: inline-flex;
            border: 1px solid rgba(2,6,23,.14);
            border-radius: 999px;
            overflow: hidden;
            background: #fff;
            }

            .langSw-chip{
            border: 0;
            background: transparent;
            padding: 5px 8px;   /* 👈 más pequeño */
            cursor: pointer;
            font-weight: 950;
            font-size: .85rem;  /* 👈 más pequeño */
            color: rgba(2,6,23,.80);
            }

            .langSw-chip.active{
            background: rgba(2,6,23,.92);
            color: #fff;
            }
          /* Oculta "Language" en móvil */
          @media (max-width: 640px){
            .langDd-label{
              display: none;
            }
          }


        `}</style>
      </>
  );
}
